/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import { Call, CallDirection, CallDisconnectStatus, CallStatus, CallWithTimestamp } from './typings';
import { useCallOperations } from './hooks/use-call-operations';
import { getCurrentUserId } from '../../infra/firebase/init';
import CallQueueBanner from './CallQueueBanner';
import { getCallsUpdateApi, getNewReconnectSDPApi } from './request';
import useInfinitePolling from '../../shared/hooks/use-infinite-poll';
import { useUserPermissions } from '../../shared/hooks/use-permission';
import { useAtom } from 'jotai';
import { activeCallIdAtom, callsAtom } from './states';
import useWabaLevelPermission from '../../shared/hooks/use-waba-level-permission';
import { Permissions } from '../../shared/typings/user';
import {
  CALL_UPDATES_POLL_MAX_RETRIES,
  CALL_UPDATES_POLL_RETRY_TIME_MS,
  CALL_UPDATES_POLL_TIME_MS,
  CALL_EXPIRY_DURATION_MS,
} from './constants';
import { TAB_ID } from '../../shared/utils/tab-id';
import { useCallSocketEvents } from './hooks/use-call-socket-events';
import useNotifications from '../../shared/hooks/use-notifications';
import { PlanAddonTypes } from '../renew-plan/v1/typings';
import { useSubscribedPlan } from '../renew-plan/v1/hooks/use-subscribed-plan';

const CALL_CLEANUP_DELAY = 3000; // 4 seconds

const getCallStartTime = (callStartedAt: string | null) => {
  if (!callStartedAt) return undefined;
  const callStartedAtDate = new Date(callStartedAt);
  return callStartedAtDate.getTime();
};

const CallManager: React.FC = () => {
  const [calls, setCalls] = useAtom(callsAtom);
  const [activeCallId, setActiveCallId] = useAtom(activeCallIdAtom);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingCall, setPendingCall] = useState<Call | null>(null);
  const currentUserId = getCurrentUserId();
  const { hasPermission, getWabasWithPermission } = useWabaLevelPermission();
  const { addNotification } = useNotifications();
  const { isAddonPresent } = useSubscribedPlan();

  const wabasWithReceiveCallPermissionPresent = useMemo(() => {
    return getWabasWithPermission(Permissions.RECEIVE_CALL).wabas?.length > 0;
  }, [getWabasWithPermission]);

  const wabasWithInitiateCallPermissionPresent = useMemo(() => {
    return getWabasWithPermission(Permissions.INITIATE_CALL).wabas?.length > 0;
  }, [getWabasWithPermission]);

  const isWaVoiceAvailable = isAddonPresent(PlanAddonTypes.WaVoiceAddon);

  const { data, error } = useInfinitePolling(getCallsUpdateApi, {
    interval: CALL_UPDATES_POLL_TIME_MS,
    maxRetries: CALL_UPDATES_POLL_MAX_RETRIES,
    retryDelay: CALL_UPDATES_POLL_RETRY_TIME_MS,
    enabled: isWaVoiceAvailable && (wabasWithReceiveCallPermissionPresent || wabasWithInitiateCallPermissionPresent),
    dependencies: [],
  });

  const getNewReconnectSDP = async (callId: string, callOriginType: CallDirection, switchCall = false) => {
    return getNewReconnectSDPApi({
      callId,
      callOriginType,
      switchCall,
    });
  };

  useCallSocketEvents();
  const { cleanupCall, removeCallById } = useCallOperations({
    onCallPicked: (callId) => {},
    onCallCreated: (callId) => {},
    onCallRemoved: (callId) => {},
  });

  useEffect(() => {
    if (!error) {
      setCalls((prevCalls) => {
        let updatedCalls = [...prevCalls];

        data?.activeCalls.forEach(async (apiCall) => {
          try {
            if (apiCall.callOriginType === CallDirection.INCOMING) {
              const hasReceiveCallPermission = hasPermission(apiCall.integrationId, Permissions.RECEIVE_CALL).hasAccess;
              if (!apiCall.metadata?.offerForCallee || !hasReceiveCallPermission) {
                return;
              }
              const existingCallIndex = updatedCalls.findIndex((call) => call.callId === apiCall.callId);

              if (existingCallIndex === -1) {
                const { data: newSDP } =
                  apiCall.status === CallStatus.IN_PROGRESS && apiCall.deviceId === TAB_ID
                    ? await getNewReconnectSDP(apiCall.callId, CallDirection.INCOMING)
                    : {
                        data: undefined,
                      };

                const newCall: CallWithTimestamp = {
                  callId: apiCall.callId,
                  customerName: apiCall.customerName,
                  customerNumber: apiCall.customerNumber,
                  customerId: apiCall.customerId,
                  wabaNumber: apiCall.wabaNumber,
                  wabaName: apiCall.wabaName,
                  integrationId: apiCall.integrationId,
                  status: apiCall.status === CallStatus.IN_PROGRESS ? CallStatus.INITIATED : apiCall.status, // INITIATED is for reconnect case
                  direction: CallDirection.INCOMING,
                  sdp: newSDP ? newSDP.sdp : apiCall.metadata.offerForCallee.sdp,
                  sdpType: newSDP ? newSDP.type : apiCall.metadata.offerForCallee.type,
                  callStartTime:
                    apiCall.status === CallStatus.IN_PROGRESS ? getCallStartTime(apiCall.callStartedAt) : undefined,
                  dateCreated: apiCall.dateCreated,
                  // Important: we need to set shouldConnect to true for reconnect case
                  shouldConnect: apiCall.deviceId === TAB_ID && apiCall.status === CallStatus.IN_PROGRESS,
                  reconnect: apiCall.status === CallStatus.IN_PROGRESS,
                  switchCall: apiCall.deviceId !== TAB_ID && apiCall.status === CallStatus.IN_PROGRESS,
                  // new date time stamp 60 sec more than dateCreated
                  expiryTimestamp: apiCall.dateCreated
                    ? new Date(apiCall.dateCreated).getTime() + CALL_EXPIRY_DURATION_MS
                    : undefined,
                };
                updatedCalls.push(newCall);
                if (prevCalls.length === 0 || apiCall.status === CallStatus.IN_PROGRESS) {
                  setActiveCallId(apiCall.callId);
                }
              } else {
                // if call is in_progess but tabId is equat to apiCall.deviceId, mark it as switch call
                if (apiCall.status === CallStatus.IN_PROGRESS && !!apiCall.deviceId && apiCall.deviceId !== TAB_ID) {
                  updatedCalls[existingCallIndex] = {
                    ...updatedCalls[existingCallIndex],
                    status: CallStatus.INITIATED,
                    switchCall: true,
                    reconnect: true,
                    shouldConnect: false,
                  };
                }
              }
            } else if (apiCall.callOriginType === CallDirection.OUTGOING) {
              const hasInitiateCallPermission = hasPermission(
                apiCall.integrationId,
                Permissions.INITIATE_CALL
              ).hasAccess;
              if (!hasInitiateCallPermission) {
                return;
              }
              const existingCallIndex = updatedCalls.findIndex((call) => call.callId === apiCall.callId);
              if (existingCallIndex === -1) {
                if (!apiCall.deviceId || apiCall.deviceId !== TAB_ID) return; // NOTE: no switch for outgoing calls for now
                const newCall: CallWithTimestamp = {
                  callId: apiCall.callId,
                  customerName: apiCall.customerName,
                  customerNumber: apiCall.customerNumber,
                  customerId: apiCall.customerId,
                  wabaNumber: apiCall.wabaNumber,
                  wabaName: apiCall.wabaName,
                  integrationId: apiCall.integrationId,
                  status: apiCall.status === CallStatus.IN_PROGRESS ? CallStatus.INITIATED : apiCall.status, // INITIATED is for reconnect case
                  direction: CallDirection.OUTGOING,
                  sdp: '',
                  sdpType: '',
                  callStartTime:
                    apiCall.status === CallStatus.IN_PROGRESS ? getCallStartTime(apiCall.callStartedAt) : undefined,
                  dateCreated: apiCall.dateCreated,
                  // Important: we need to set shouldConnect to true for reconnect case
                  shouldConnect: apiCall.deviceId === TAB_ID && apiCall.status === CallStatus.IN_PROGRESS,
                  reconnect: apiCall.status === CallStatus.IN_PROGRESS,
                  switchCall: apiCall.deviceId !== TAB_ID && apiCall.status === CallStatus.IN_PROGRESS,
                  // new date time stamp 60 sec more than dateCreated
                  expiryTimestamp: apiCall.dateCreated
                    ? new Date(apiCall.dateCreated).getTime() + CALL_EXPIRY_DURATION_MS
                    : undefined,
                };
                updatedCalls.push(newCall);
                if (prevCalls.length === 0 || apiCall.status === CallStatus.IN_PROGRESS) {
                  setActiveCallId(apiCall.callId);
                }
              } else {
                if (
                  updatedCalls[existingCallIndex].status !== CallStatus.IN_PROGRESS &&
                  apiCall.status === CallStatus.IN_PROGRESS
                ) {
                  updatedCalls[existingCallIndex] = {
                    ...updatedCalls[existingCallIndex],
                    status: apiCall.status,
                    callStartTime: getCallStartTime(apiCall.callStartedAt),
                  };
                }
              }
            }
          } catch (error) {
            console.error('Error processing call update:', error, apiCall);
          }
        });

        // rmv calls which are no more in updates api
        updatedCalls = updatedCalls.filter((call) => {
          if (data?.activeCalls.some((apiCall) => apiCall.callId === call.callId)) return true;
          // check for expired call (initiated more than 60 sec ago)
          if (call.status === CallStatus.INITIATED && call.expiryTimestamp && +call.expiryTimestamp < Date.now())
            return false;
          // if call is not in api call but was created within 12 seconds (2 updates api calls), keep it
          if (call.dateCreated && new Date(call.dateCreated).getTime() + 2 * CALL_UPDATES_POLL_TIME_MS > Date.now())
            return true;
          return false;
        });

        return updatedCalls;
      });
    }
  }, [data, error]);

  const handleAcceptCall = (incomingCall: Call, switchCall?: boolean) => {
    const activeCall = calls.find(
      (call) => call.status === CallStatus.IN_PROGRESS || call.status === CallStatus.Connecting
    );

    if (activeCall) {
      setPendingCall(incomingCall);
      setShowConfirmation(true);
    } else {
      answerCall(incomingCall, switchCall);
    }
  };

  const answerCall = async (call: Call, switchCall?: boolean) => {
    if (switchCall) {
      try {
        const resp = await getNewReconnectSDP(call.callId, CallDirection.INCOMING, switchCall);
        const newSdp = resp.data;
        const callStartTime = resp.callStartedAt;
        setCalls((prev) =>
          prev.map((c) => ({
            ...c,
            shouldConnect: c.callId === call.callId ? true : c.shouldConnect,
            // we nned to set reconnect to true for switch call
            reconnect: c.callId === call.callId && switchCall ? true : c.reconnect,
            switchCall: c.callId === call.callId && switchCall ? switchCall : c.switchCall,
            sdp: c.callId === call.callId && switchCall ? newSdp?.sdp : c.sdp,
            callStartTime: callStartTime ? getCallStartTime(callStartTime) : undefined,
          }))
        );
      } catch (err) {
        console.error(error);
        addNotification({
          type: 'error',
          message: (err as Error).message || 'Error encountered while switching call',
        });
      }
    } else {
      setCalls((prev) =>
        prev.map((c) => ({
          ...c,
          shouldConnect: c.callId === call.callId ? true : c.shouldConnect,
        }))
      );
    }

    setActiveCallId(call.callId);
  };

  const onAcceptStateHandler = (call: Call, state: CallStatus) => {
    setCalls((prev) => {
      return prev.map((c) => {
        if (c.callId === call.callId || c.tempCallId === call.callId) {
          return {
            ...c,
            status: state,
            ...(state === CallStatus.IN_PROGRESS
              ? {
                  callStartTime: c.callStartTime ? c.callStartTime : Date.now(),
                }
              : {}), // if call is not in progress, don't update callStartTime
          };
        }
        return c;
      });
    });
  };

  const handleDisconnect = (status: CallDisconnectStatus, disconnectedCall: Call, removeWithoutDelay = false) => {
    if (removeWithoutDelay) {
      removeCallById(disconnectedCall.callId);
      return;
    }
    // Mark as disconnected first, then cleanup after delay
    setCalls((prev) => prev.map((call) => (call.callId === disconnectedCall.callId ? { ...call, status } : call)));
    cleanupCall(disconnectedCall.callId);
  };

  const handleSwitchCall = () => {
    if (pendingCall) {
      const activeCall = calls.find(
        (call) => call.status === CallStatus.IN_PROGRESS || call.status === CallStatus.Connecting
      );
      if (activeCall) {
        // Trigger force disconnect
        setCalls((prev) =>
          prev.map((c) => ({
            ...c,
            shouldForceDisconnect: c.callId === activeCall.callId ? true : c.shouldForceDisconnect,
          }))
        );
        cleanupCall(activeCall.callId);
      }
      answerCall(pendingCall);

      setPendingCall(null);
      setShowConfirmation(false);
    }
  };

  const handleCallClick = (call: Call) => {
    setActiveCallId(call.callId);
  };

  const handleCallMinimize = (call: Call) => {
    if (activeCallId === call.callId) {
      setActiveCallId(null);
    }
  };

  const handleCallClose = (call: Call) => {
    setCalls((prev) => prev.filter((c) => c.callId !== call.callId));

    // If the closed call was active, set the next call as active (if any)
    if (activeCallId === call.callId) {
      const remainingCalls = calls.filter((c) => c.callId !== call.callId);
      setActiveCallId(remainingCalls.length > 0 ? remainingCalls[0].callId : null);
    }
  };

  const orderedCalls = useMemo(() => {
    if (!activeCallId) return calls;
    const active = calls.find((c) => c.callId === activeCallId);
    const others = calls.filter((c) => c.callId !== activeCallId);
    return active ? [active, ...others] : calls;
  }, [calls, activeCallId]);

  return (
    <>
      <CallQueueBanner
        calls={orderedCalls}
        activeCallId={activeCallId}
        onSelect={handleCallClick}
        onMinimize={handleCallMinimize}
        onCloseChip={handleCallClose}
        onDisconnect={handleDisconnect}
        onAcceptStateChange={(call, state) => onAcceptStateHandler(call, state)}
        onRequestAccept={handleAcceptCall}
      />

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmation}
        onClose={() => {
          setShowConfirmation(false);
          setPendingCall(null);
        }}
      >
        <DialogTitle>Switch Call?</DialogTitle>
        <DialogContent>
          <Typography>
            You're already on a call. Accepting this call from{' '}
            {pendingCall?.customerName ?? pendingCall?.customerNumber} will end your current call. Do you want to
            switch?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowConfirmation(false);
              setPendingCall(null);
            }}
            color="primary"
          >
            Cancel
          </Button>
          <Button onClick={handleSwitchCall} color="error" variant="contained">
            End Current & Switch
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CallManager;
